// src/features/crypto-ranking/components/RiskReturnScatter.tsx
//
// Cross-sectional Risk vs Return scatter for the crypto universe.
//   - X axis: annualized volatility (risk), shown as a percentage.
//   - Y axis: the composite Forge Ultra Crypto Signal (expected return
//     proxy).
// Each dot is one coin; the upper-left quadrant (low risk / high signal)
// is the institutionally desirable corner. Built on Recharts — the same
// charting engine the equities / ETF / commodities scatters use — so the
// dark tooltip + responsive sizing match the rest of the dashboard.

import {
    CartesianGrid,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
    ZAxis,
} from 'recharts';

import { baseAsset, type UniverseRow } from '../api';

interface RiskReturnScatterProps {
    data: UniverseRow[];
    /** Optional symbol to emphasise (e.g. a hovered table row). */
    highlightSymbol?: string | null;
}

interface ScatterDatum {
    x: number; // volatility as a percentage
    y: number; // forge_ultra_signal
    symbol: string;
    name: string;
    rank: number;
}

const AXIS_TICK = { fontSize: 11, fill: 'currentColor' };

const ScatterTooltip = ({
    active,
    payload,
}: {
    active?: boolean;
    payload?: { payload: ScatterDatum }[];
}) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">
                {d.name}{' '}
                <span className="font-normal text-slate-500 dark:text-slate-400">
                    #{d.rank}
                </span>
            </p>
            <p className="text-slate-500 dark:text-slate-400">{d.symbol}</p>
            <p className="mt-1 text-slate-700 dark:text-slate-300">
                Signal: <span className="font-mono">{d.y.toFixed(2)}</span>
            </p>
            <p className="text-slate-700 dark:text-slate-300">
                Vol: <span className="font-mono">{d.x.toFixed(1)}%</span>
            </p>
        </div>
    );
};

export const RiskReturnScatter = ({
    data,
    highlightSymbol,
}: RiskReturnScatterProps) => {
    const points: ScatterDatum[] = data.map((r) => ({
        x: r.annualized_volatility * 100,
        y: r.forge_ultra_signal,
        symbol: r.symbol,
        name: baseAsset(r.symbol),
        rank: r.universe_rank,
    }));

    const highlighted = highlightSymbol
        ? points.filter((p) => p.symbol === highlightSymbol)
        : [];

    return (
        <div
            className="w-full h-80 text-slate-500 dark:text-slate-400"
            role="img"
            aria-label="Risk versus return scatter of the crypto universe"
        >
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 16, right: 24, bottom: 32, left: 8 }}>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        className="stroke-slate-200 dark:stroke-slate-700"
                    />
                    <XAxis
                        type="number"
                        dataKey="x"
                        name="Volatility"
                        unit="%"
                        tick={AXIS_TICK}
                        tickLine={false}
                        label={{
                            value: 'Annualized Volatility (%)',
                            position: 'bottom',
                            offset: 12,
                            fontSize: 12,
                            fill: 'currentColor',
                        }}
                    />
                    <YAxis
                        type="number"
                        dataKey="y"
                        name="Forge Ultra Signal"
                        tick={AXIS_TICK}
                        tickLine={false}
                        width={44}
                        label={{
                            value: 'Forge Ultra Signal',
                            angle: -90,
                            position: 'insideLeft',
                            fontSize: 12,
                            fill: 'currentColor',
                        }}
                    />
                    <ZAxis range={[40, 40]} />
                    <Tooltip
                        content={<ScatterTooltip />}
                        cursor={{ strokeDasharray: '3 3' }}
                    />
                    <Scatter
                        data={points}
                        fill="#f7931a"
                        fillOpacity={0.6}
                    />
                    {highlighted.length > 0 && (
                        <Scatter
                            data={highlighted}
                            fill="#0ea5e9"
                            shape="circle"
                        />
                    )}
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
};

export default RiskReturnScatter;
